/**
 * MapOverlay Component
 *
 * Displays store information overlay on the left half of the map.
 * Shows address, directions button, wheelchair access, and parking info.
 */

import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';

import {
  Box,
  Button,
  HStack,
  Text,
  useMultiStyleConfig,
  VStack,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {ParkingIcon, WheelchairIcon} from '@amblique/king-living/app/components/icons';

/**
 * Normalize SFCC store address fields (top-level vs nested store.address).
 * Matches fallbacks used in store-tile.jsx so city/state/postcode are not dropped.
 *
 * @param {Object} store - Store from useStores / page context
 * @returns {{address1: string, address2: string, city: string, stateCode: string, postalCode: string}}
 */
const getStoreAddressParts = (store) => ({
  address1: String(store.address1 || store.address?.address1 || '').trim(),
  address2: String(store.address2 || store.address?.address2 || '').trim(),
  city: String(store.city || store.address?.city || '').trim(),
  stateCode: String(
    store.stateCode || store.address?.state || store.address?.stateCode || ''
  ).trim(),
  postalCode: String(store.postalCode || store.address?.postalCode || '').trim(),
});

/**
 * Build the `q` value for legacy Google Maps links to align with kingliving.com
 * (e.g. street,, suburb, state postcode,15).
 *
 * @param {{address1: string, address2: string, city: string, stateCode: string, postalCode: string}} parts
 * @returns {string}
 */
const buildGoogleMapsSearchQuery = (parts) => {
  const {address1, address2, city, stateCode, postalCode} = parts;
  const stateZip = [stateCode, postalCode].filter(Boolean).join(' ');
  const locality = [city, stateZip].filter(Boolean).join(', ');

  if (!address1 && !locality) {
    return '';
  }

  let core;

  if (address1 && locality) {
    // Live uses a double comma when line 2 is absent: "69-73 Rd,, Annandale, NSW 2038"
    core = address2 ? `${address1}, ${address2}, ${locality}` : `${address1},, ${locality}`;
  } else {
    core = address1 || locality;
  }

  return `${core},15`;
};

/**
 * MapOverlay component
 *
 * @param {Object} props - Component props
 * @param {Object} props.store - SFCC Store data
 * @param {boolean} props.wheelchairAccessible - Wheelchair accessible flag from Contentful
 * @param {string} props.parkingAddress - Parking address from Contentful
 * @returns {React.ReactElement}
 */
export const MapOverlay = ({
  parkingAddress = '',
  store,
  wheelchairAccessible = false,
  ...props
}) => {
  const styles = useMultiStyleConfig('contentful/showroom-details');

  if (!store) {
    return null;
  }

  const addr = getStoreAddressParts(store);
  const stateZip = [addr.stateCode, addr.postalCode].filter(Boolean).join(' ');
  const cityStateLine = [addr.city, stateZip].filter(Boolean).join(' ');
  const fullAddress = [addr.address1, addr.address2, cityStateLine].filter(Boolean).join(', ');

  // KINDW-672: address-shaped `q` matches live Maps search; KINDW-666: formatted store name prefix
  const addressMapsQuery = buildGoogleMapsSearchQuery(addr);
  const displayName = store.name;
  const mapsQuery =
    addressMapsQuery && displayName
      ? `${displayName}, ${addressMapsQuery}`
      : addressMapsQuery || (displayName ? `${displayName},15` : '');

  // Legacy maps?q=... matches live hover URL shape (KINDW-672); not /dir/ from user location.
  const mapsUrl = mapsQuery ?
    `https://www.google.com/maps?q=${encodeURIComponent(mapsQuery)}` :
    null;

  const hasParkingInfo = !!parkingAddress;
  const hasAccessibilityInfo = wheelchairAccessible || hasParkingInfo;

  return (
    <Box
      className="map-overlay"
      {...styles.mapOverlay}
      {...props}
    >
      <VStack align="stretch" spacing={{base: '16px', lg: '16x'}}>
        {/* Address Header */}
        <Text {...styles.mapOverlayAddress}>
          {fullAddress}
        </Text>

        {/* Get directions: real link avoids popup quirks and matches live Maps search behavior */}
        <Button
          as={mapsUrl ? 'a' : 'button'}
          href={mapsUrl ?? undefined}
          rel={mapsUrl ? 'noopener noreferrer' : undefined}
          target={mapsUrl ? '_blank' : undefined}
          {...styles.mapOverlayButton}
          isDisabled={!mapsUrl}
        >
          <FormattedMessage
            defaultMessage="Get directions"
            id="showroom_details.map_overlay.get_directions"
          />
        </Button>

        <Box as="span" {...styles.mapOverlayDivider} />

        {/* Accessibility & Parking Info */}
        {hasAccessibilityInfo && (
          <VStack {...styles.mapOverlayInfoSection}>
            {/* Wheelchair Access */}
            {wheelchairAccessible && (
              <HStack {...styles.mapOverlayInfoItem}>
                <WheelchairIcon {...styles.mapOverlayIcon} />
                <Text {...styles.mapOverlayInfoText}>
                  <FormattedMessage
                    defaultMessage="Wheelchair access"
                    id="showroom_details.map_overlay.wheelchair_access"
                  />
                </Text>
              </HStack>
            )}

            {/* Parking Info */}
            {hasParkingInfo && (
              <VStack {...styles.mapOverlayParkingContainer}>
                <HStack {...styles.mapOverlayInfoItem}>
                  <ParkingIcon {...styles.mapOverlayIcon} />
                  <Text {...styles.mapOverlayInfoText} color="#3D3D3D">
                    <FormattedMessage
                      defaultMessage="Carpark available"
                      id="showroom_details.map_overlay.carpark_available"
                    />
                  </Text>
                </HStack>
                <Text {...styles.mapOverlayInfoText}>
                  {parkingAddress}
                </Text>
              </VStack>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

MapOverlay.displayName = 'MapOverlay';

MapOverlay.propTypes = {
  parkingAddress: PropTypes.string,
  store: PropTypes.shape({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    name: PropTypes.string,
    postalCode: PropTypes.string,
    stateCode: PropTypes.string,
  }),
  wheelchairAccessible: PropTypes.bool,
};

export default MapOverlay;
